import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Offcanvas, Form } from "react-bootstrap";
import { MdArrowBack } from "react-icons/md";
import * as apicall from "../../components/Apicall";
import Skeleton from "../../components/Loader/Skeleton";

const ViewProfile = () => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEditSidebar, setShowEditSidebar] = useState(false);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await apicall.get("ezpMyProfile");

        // Transform the API response to match your expected data structure
        const transformedData = {
          personalDetails: {
            fullName: `${response.data[0].first_name} ${
              response.data[0].middle_name || ""
            } ${response.data[0].last_name || ""}`.trim(),
            gender: response.data[0].gender === 1 ? "Male" : "Female",
            dateOfBirth: response.data[0].dob || "",
            totalExperience: response.data[0].practice_start
              ? calculateExperience(response.data[0].practice_start)
              : "",
          },
          addressDetails: {
            address: response.data[0].address || "",
            city: response.data[0].city_name || "",
            state: response.data[0].state_name || "",
            country: response.data[0].country_name || "",
          },
          contactDetails: {
            phoneNumber: response.data[0].phone_number || "",
            email: response.data[0].email || "",
          },
          profileImage:
            response.data[0].profile_image ||
            "https://img.freepik.com/free-photo/doctor-with-his-arms-crossed-white-background_1368-5790.jpg",
        };

        setUserData(transformedData);
        setError(null);
      } catch (err) {
        console.error("Error fetching profile data:", err);
        setError("Failed to load profile data. Please try again later.");
        // You might want to set some default data here in case of error
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditProfile = () => {
    setShowEditSidebar(true);
  };

  const calculateExperience = (practiceStart) => {
    if (!practiceStart) return "0";
    const startDate = new Date(practiceStart);
    const currentDate = new Date();
    const years = currentDate.getFullYear() - startDate.getFullYear();
    return `${years} Years Experience`;
  };

  const EditProfileSidebar = () => {
    const [formData, setFormData] = useState({
      first_name: userData?.personalDetails.fullName.split(" ")[0] || "",
      middle_name: userData?.personalDetails.fullName.split(" ")[1] || "",
      last_name:
        userData?.personalDetails.fullName.split(" ").slice(2).join(" ") || "",
      gender: userData?.personalDetails.gender === "Male" ? 1 : 2,
      dob: userData?.personalDetails.dateOfBirth || "",
      address: userData?.addressDetails.address || "",
      city_id: "",
      state_id: "",
      country_id: "",
      phone_number: userData?.contactDetails.phoneNumber || "",
      email: userData?.contactDetails.email || "",
    });

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const fetchCountries = async () => {
      try {
        const response = await apicall.get("country");

        if (response?.data) {
          // Handle both array and object responses
          const countriesData = Array.isArray(response.data)
            ? response.data
            : [response.data];

          setCountries(countriesData);
          return countriesData;
        }

        return [];
      } catch (error) {
        console.error("Error fetching countries:", error);
        return [];
      }
    };

    const fetchStates = async (countryId) => {
      if (!countryId) {
        return [];
      }
      try {
        const response = await apicall.get(`state/${countryId}`);

        if (response?.data) {
          const statesData = Array.isArray(response.data)
            ? response.data
            : [response.data];

          setStates(statesData);
          return statesData;
        }

        setStates([]);
        return [];
      } catch (error) {
        console.error("Error fetching states:", error);
        setStates([]);
        return [];
      }
    };

    const fetchCities = async (stateId) => {
      if (!stateId) {
        return [];
      }
      try {
        const response = await apicall.get(`city/${stateId}`);

        if (response?.data) {
          const citiesData = Array.isArray(response.data)
            ? response.data
            : [response.data];

          setCities(citiesData);
          return citiesData;
        }

        setCities([]);
        return [];
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
        return [];
      }
    };

    // Initial data load
    useEffect(() => {
      const initializeData = async () => {
        try {
          setIsLoading(true);

          // Fetch countries first
          const countriesData = await fetchCountries();

          if (countriesData.length > 0) {
            // Find country by name first
            const currentCountry = countriesData.find((c) => {
              const countryName = c.country_name || c.name;
              const searchName = userData?.addressDetails?.country;

              return countryName?.toLowerCase() === searchName?.toLowerCase();
            });

            if (currentCountry) {
              const countryId = currentCountry.id || currentCountry.country_id;

              setFormData((prev) => ({ ...prev, country_id: countryId }));

              // Fetch states for the selected country
              const statesData = await fetchStates(countryId);

              if (statesData.length > 0) {
                // Find state by name
                const currentState = statesData.find((s) => {
                  const stateName = s.state_name || s.name;
                  const searchName = userData?.addressDetails?.state;

                  return stateName?.toLowerCase() === searchName?.toLowerCase();
                });

                if (currentState) {
                  const stateId = currentState.id || currentState.state_id;

                  setFormData((prev) => ({ ...prev, state_id: stateId }));

                  // Fetch cities for the selected state
                  const citiesData = await fetchCities(stateId);

                  if (citiesData.length > 0) {
                    // Find city by name
                    const currentCity = citiesData.find((c) => {
                      const cityName = c.city_name || c.name;
                      const searchName = userData?.addressDetails?.city;

                      return (
                        cityName?.toLowerCase() === searchName?.toLowerCase()
                      );
                    });

                    if (currentCity) {
                      const cityId = currentCity.id || currentCity.city_id;

                      setFormData((prev) => ({ ...prev, city_id: cityId }));
                    }
                  }
                }
              }
            }
          }
        } catch (error) {
          console.error("Error initializing data:", error);
        } finally {
          setIsLoading(false);
        }
      };

      if (userData) {
        initializeData();
      }
    }, [userData]);

    const handleInputChange = async (e) => {
      const { name, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Handle cascading updates
      if (name === "country_id") {
        if (value) {
          await fetchStates(value);
          // Reset state and city when country changes
          setFormData((prev) => ({
            ...prev,
            state_id: "",
            city_id: "",
          }));
          setCities([]);
        } else {
          setStates([]);
          setCities([]);
        }
      } else if (name === "state_id") {
        if (value) {
          await fetchCities(value);
          // Reset city when state changes
          setFormData((prev) => ({
            ...prev,
            city_id: "",
          }));
        } else {
          setCities([]);
        }
      }
    };

    const handleSubmit = async () => {
      try {
        // Validate required fields
        if (!formData.country_id || !formData.state_id || !formData.city_id) {
          alert("Please select country, state, and city");
          return;
        }

        const response = await apicall.post(formData, "updateUser");

        if (response.status === 200) {
          // alert('Profile updated successfully');
          setShowEditSidebar(false);
          // Refresh the profile data
          window.location.reload();
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        console.error("Error details:", error.response?.data);
        alert(
          error.response?.data?.message ||
            "Failed to update profile. Please try again."
        );
      }
    };

    const SidebarSkeleton = () => {
      return (
        <div className="p-4">
          <div className="mb-4">
            <Skeleton width="120px" height="24px" marginBottom="16px" />
            {[...Array(5)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="100px" height="20px" marginBottom="8px" />
                <Skeleton width="100%" height="38px" />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <Skeleton width="120px" height="24px" marginBottom="16px" />
            {[...Array(4)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="100px" height="20px" marginBottom="8px" />
                <Skeleton width="100%" height="38px" />
              </div>
            ))}
          </div>

          <div className="mb-4">
            <Skeleton width="120px" height="24px" marginBottom="16px" />
            {[...Array(2)].map((_, index) => (
              <div key={index} className="mb-3">
                <Skeleton width="100px" height="20px" marginBottom="8px" />
                <Skeleton width="100%" height="38px" />
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <Offcanvas
        show={showEditSidebar}
        onHide={() => setShowEditSidebar(false)}
        placement="end"
        className="w-500"
        style={{ top: "64px" }}
      >
        <Offcanvas.Header closeButton className="custom-border-bottom p-4">
          <Offcanvas.Title className="heading">Edit Profile</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {isLoading ? (
            <SidebarSkeleton />
          ) : (
            <div className="p-4">
              <Form>
                <div className="mb-4">
                  <h6 className="profile-section-heading">Personal Details</h6>
                  <Form.Group className="mb-3">
                    <Form.Label>First Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Middle Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="middle_name"
                      value={formData.middle_name}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Gender</Form.Label>
                    <Form.Select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value={1}>Male</option>
                      <option value={2}>Female</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Date of Birth</Form.Label>
                    <Form.Control
                      type="date"
                      name="dob"
                      value={formData.dob}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </Form.Group>
                </div>

                <div className="mb-4">
                  <h6 className="profile-section-heading">Address Details</h6>
                  <Form.Group className="mb-3">
                    <Form.Label>Address Line</Form.Label>
                    <Form.Control
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Select
                      name="country_id"
                      value={formData.country_id}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name || country.country_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>State</Form.Label>
                    <Form.Select
                      name="state_id"
                      value={formData.state_id}
                      onChange={handleInputChange}
                      className="form-input"
                      disabled={!formData.country_id}
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Select
                      name="city_id"
                      value={formData.city_id}
                      onChange={handleInputChange}
                      className="form-input"
                      disabled={!formData.state_id}
                    >
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id}>
                          {city.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <div className="mb-4">
                  <h6 className="profile-section-heading">Contact Details</h6>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleInputChange}
                      className="form-input"
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </Form.Group>
                </div>
              </Form>
            </div>
          )}
          <div className="position-sticky bottom-0 bg-white custom-border-top p-4 d-flex gap-3">
            <Button
              variant="light"
              onClick={() => setShowEditSidebar(false)}
              className="custom-radius btn-outline-borders flex-grow-1"
              style={{ height: "40px" }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              className="btn-custom-danger custom-radius flex-grow-1"
              onClick={handleSubmit}
              style={{ height: "40px" }}
            >
              Save Changes
            </Button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    );
  };

  const ProfileSkeleton = () => {
    return (
      <div className="px-4 py-3">
        <div className="d-flex align-items-center mb-4">
          <Skeleton width="80px" height="24px" />
        </div>

        <div className="bg-white border-box p-4 rounded-4">
          <div className="d-flex gap-5">
            {/* Left Column - Profile Image Skeleton */}
            <div
              className="d-flex flex-column align-items-center"
              style={{ width: "258px" }}
            >
              <Skeleton width="258px" height="248px" marginBottom="16px" />
              <Skeleton width="121px" height="32px" />
            </div>

            {/* Right Column - Details Skeleton */}
            <div className="flex-grow-1">
              <div className="row g-4">
                {/* Personal Details Skeleton */}
                <div className="col-md-4">
                  <Skeleton width="120px" height="24px" marginBottom="16px" />
                  <div className="d-flex flex-column gap-3">
                    {[...Array(4)].map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          width="80px"
                          height="16px"
                          marginBottom="8px"
                        />
                        <Skeleton width="140px" height="20px" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Address Details Skeleton */}
                <div className="col-md-4">
                  <Skeleton width="120px" height="24px" marginBottom="16px" />
                  <div className="d-flex flex-column gap-3">
                    {[...Array(4)].map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          width="80px"
                          height="16px"
                          marginBottom="8px"
                        />
                        <Skeleton width="140px" height="20px" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Details Skeleton */}
                <div className="col-md-4">
                  <Skeleton width="120px" height="24px" marginBottom="16px" />
                  <div className="d-flex flex-column gap-3">
                    {[...Array(2)].map((_, index) => (
                      <div key={index}>
                        <Skeleton
                          width="80px"
                          height="16px"
                          marginBottom="8px"
                        />
                        <Skeleton width="140px" height="20px" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (error) {
    return <div className="p-4 text-danger">{error}</div>;
  }

  if (!userData) {
    return <div className="p-4">No profile data available</div>;
  }

  return (
    <div className="px-4 py-3">
      <div className="d-flex align-items-center mb-4">
        <Button
          variant="link"
          className="back-button text-dark text-decoration-none p-0"
          onClick={handleBack}
        >
          <MdArrowBack size={20} /> BACK
        </Button>
      </div>

      <div className="bg-white border-box p-4 rounded-4">
        <div className="d-flex gap-5">
          {/* Left Column - Profile Image */}
          <div
            className="d-flex flex-column align-items-center"
            style={{ width: "258px" }}
          >
            <img
              src={userData.profileImage}
              alt={userData.personalDetails.fullName}
              className="border-box profile-image-shadow"
              style={{
                width: "258px",
                height: "248px",
                borderRadius: "20px",
                objectFit: "cover",
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src =
                  "https://via.placeholder.com/300x300?text=No+Image";
              }}
            />
            <div className="d-flex justify-content-center gap-3 mt-2 mb-2 w-100">
              <Button
                variant="danger"
                onClick={handleEditProfile}
                className="btn-custom-danger custom-radius d-flex align-items-center justify-content-center"
                style={{
                  width: "121px",
                  height: "32px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Edit Profile
              </Button>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="flex-grow-1">
            <div className="row g-4">
              {/* Personal Details */}
              <div className="col-md-4">
                <h6 className="profile-section-heading">Personal Details</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="label mb-1">Full Name</div>
                    <div className="content fw-bold text-dark">
                      {userData.personalDetails.fullName}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Gender</div>
                    <div className="content fw-bold text-dark">
                      {userData.personalDetails.gender}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Date of Birth</div>
                    <div className="content fw-bold text-dark">
                      {userData.personalDetails.dateOfBirth}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Total Experience</div>
                    <div className="content fw-bold text-dark">
                      {userData.personalDetails.totalExperience}
                    </div>
                  </div>
                </div>
              </div>

              {/* Address Details */}
              <div className="col-md-4">
                <h6 className="profile-section-heading">Address Details</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="label mb-1">Address Line</div>
                    <div className="content fw-bold text-dark">
                      {userData.addressDetails.address}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">City</div>
                    <div className="content fw-bold text-dark">
                      {userData.addressDetails.city}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">State</div>
                    <div className="content fw-bold text-dark">
                      {userData.addressDetails.state}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Country</div>
                    <div className="content fw-bold text-dark">
                      {userData.addressDetails.country}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Details */}
              <div className="col-md-4">
                <h6 className="profile-section-heading">Contact Details</h6>
                <div className="d-flex flex-column gap-3">
                  <div>
                    <div className="label mb-1">Phone Number</div>
                    <div className="content fw-bold text-dark">
                      {userData.contactDetails.phoneNumber}
                    </div>
                  </div>
                  <div>
                    <div className="label mb-1">Email</div>
                    <div className="content fw-bold text-dark">
                      {userData.contactDetails.email}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditProfileSidebar />
    </div>
  );
};

export default ViewProfile;
